import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCategory, setActiveCategory] = useState(slug || 'guoman');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeCategory]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const filteredComics = useMemo(() => {
    let result = comics;

    if (activeCategory) {
      result = result.filter((c) => c.category === activeCategory);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }

    return result;
  }, [activeCategory, searchQuery, sortBy]);

  const breadcrumbs = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '漫画分类', href: '#' },
  ];

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <nav className="px-4 md:px-6 py-3 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              {breadcrumbs.map((crumb, idx) => (
                <li key={crumb.label} className="flex items-center gap-2">
                  {idx > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
                  {crumb.href !== '#' ? (
                    <Link to={crumb.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-foreground-800 font-medium">{crumb.label}</span>
                  )}
                </li>
              ))}
              {searchQuery && (
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-foreground-800">搜索: {searchQuery}</span>
                </li>
              )}
            </ol>
          </div>
        </nav>

        {/* Category Header */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
              {currentCategory?.name || '漫画分类'}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {currentCategory?.description || '探索精彩漫画世界'}
            </p>
          </div>
        </section>

        {/* Category Tabs + Sort */}
        <section className="px-4 md:px-6 py-4 bg-background-50 border-b border-background-200 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 bg-background-100 rounded-full px-1 py-1 overflow-x-auto flex-nowrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-3 py-1.5 text-xs md:text-sm rounded-full transition-all whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground-500 text-xs">排序:</span>
                {[
                  { key: 'latest' as const, label: '最新' },
                  { key: 'popular' as const, label: '最热' },
                  { key: 'rating' as const, label: '评分' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`px-2.5 py-1 text-xs rounded-full transition-all whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-accent-500 text-background-50'
                        : 'text-foreground-500 hover:text-foreground-900'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comic Grid */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-50 min-h-[400px]">
          <div className="max-w-7xl mx-auto">
            {filteredComics.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                  {filteredComics.map((comic) => (
                    <ComicCard key={comic.id} {...comic} />
                  ))}
                </div>
                {filteredComics.length > 12 && (
                  <div className="mt-8 text-center">
                    <button className="px-6 py-2 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer">
                      加载更多
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300 mb-3"></i>
                <p className="text-foreground-500">没有找到相关漫画</p>
                <Link to="/" className="mt-3 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}